import { useState, useRef, useEffect, useCallback } from "react";
import { Bell, House, User, Shirt, LogOut, Shield, Trophy, CircleHelp } from "lucide-react";
import { NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { useTeam } from "../../context/TeamContext";
// Nova cross-promo banner — oculto hasta que haya usuarios. Restaurar: este
// import, el <AdBanner/> del layout, y el padding-bottom de .app-content (CSS).
// import { AdBanner } from "./AdBanner";
import { api } from "../../api/clientInstance";
import { fetchPendingFriends, acceptFriend, rejectFriend } from "../../api/friends";
import Modal from "../Modal";
import LoginForm from "../LoginForm";
import SignupForm from "../SignupForm";
import GuestRegisterModal from "../GuestRegisterModal";
import { LanguageSelector } from "../LanguageSelector";
import NewMatchButton from "../match/NewMatchButton";
import "./AppLayout.css";

const POLL_INTERVAL = 60_000;

// Easter egg message keys, escalating on repeated triple-clicks of the brand.
const EGG_KEYS = ["egg1", "egg2", "egg3", "egg4", "egg5", "egg6"];

function NotificationBell() {
  const { t }    = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open,        setOpen]        = useState(false);
  const [matches,     setMatches]     = useState([]);
  const [friendReqs,  setFriendReqs]  = useState([]);
  const [cupInvites,  setCupInvites]  = useState([]);
  const ref = useRef(null);

  const fetchPending = useCallback(() => {
    if (!user) return;
    api("/game/matches/pending").then(setMatches).catch(() => {});
    fetchPendingFriends().then(setFriendReqs).catch(() => {});
    api("/cup/invitations").then(setCupInvites).catch(() => {});
  }, [user]);

  useEffect(() => {
    fetchPending();
    const id = setInterval(fetchPending, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchPending]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleMatchClick = (matchId) => {
    setOpen(false);
    api(`/game/match/${matchId}/notif-seen`, { method: "PATCH" }).catch(() => {});
    setMatches((prev) => prev.filter((m) => m.match_id !== matchId));
    navigate(`/match/${matchId}`);
  };

  const handleFriendAction = async (id, action) => {
    await (action === "accept" ? acceptFriend(id) : rejectFriend(id));
    setFriendReqs((prev) => prev.filter((u) => u.id !== id));
  };

  const handleCupInvite = async (cupId, accept) => {
    await api(`/cup/invitation/${cupId}/respond`, { method: "PATCH", body: JSON.stringify({ accept }) });
    setCupInvites((prev) => prev.filter((c) => c.cup_id !== cupId));
  };

  const incomingMatches = matches.filter((m) => m.perspective === "away" && !m.notif_seen);
  const totalCount = incomingMatches.length + friendReqs.length + cupInvites.length;

  return (
    <div ref={ref} className="notif-wrapper">
      <button className="notif-bell-btn" onClick={() => setOpen((o) => !o)} title={t("notif.title")}>
        <Bell size={18} />
        {totalCount > 0 && <span className="notif-badge">{totalCount}</span>}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-header">{t("notif.title")}</div>
          {totalCount === 0 ? (
            <p className="notif-empty">{t("notif.empty")}</p>
          ) : (
            <ul className="notif-list">
              {friendReqs.map((u) => (
                <li key={u.id} className="notif-item notif-item--friend">
                  <span className="notif-icon">👤</span>
                  <span className="notif-text notif-text--grow">{t("notif.friendRequest", { name: u.name })}</span>
                  <button className="notif-friend-btn notif-friend-btn--accept" onClick={() => handleFriendAction(u.id, "accept")}>✓</button>
                  <button className="notif-friend-btn notif-friend-btn--reject" onClick={() => handleFriendAction(u.id, "reject")}>✕</button>
                </li>
              ))}
              {cupInvites.map((c) => (
                <li key={c.cup_id} className="notif-item notif-item--friend">
                  <span className="notif-icon">🏆</span>
                  <span className="notif-text notif-text--grow">{t("notif.cupInvite", { organizer: c.organizer, cup: c.cup_name })}</span>
                  <button className="notif-friend-btn notif-friend-btn--accept" onClick={() => handleCupInvite(c.cup_id, true)}>✓</button>
                  <button className="notif-friend-btn notif-friend-btn--reject" onClick={() => handleCupInvite(c.cup_id, false)}>✕</button>
                </li>
              ))}
              {incomingMatches.map((m) => (
                <li key={m.match_id} className="notif-item" onClick={() => handleMatchClick(m.match_id)}>
                  <span className="notif-icon">⚽</span>
                  <span className="notif-text">
                    {t("notif.challenged", { challenger: m.opponent })}
                    {m.opponent_manager && <span className="coach-sub">{m.opponent_manager}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function AppLayout({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { team, readiness } = useTeam();
  const navigate  = useNavigate();
  const location  = useLocation();
  const contentRef = useRef(null);

  // Scroll content to top on route change
  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const [showLogin,  setShowLogin]  = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showGuestRegister, setShowGuestRegister] = useState(false);

  // Easter egg: triple-click the brand (escalating messages)
  const brandClickRef = useRef({ count: 0, timer: null, level: 0 });
  const [eggKey, setEggKey] = useState(null);
  const handleBrandClick = useCallback((e) => {
    const b = brandClickRef.current;
    b.count++;
    clearTimeout(b.timer);
    if (b.count >= 3) {
      e.preventDefault();
      b.count = 0;
      const key = EGG_KEYS[Math.min(b.level, EGG_KEYS.length - 1)];
      b.level++;
      setEggKey(key);
      setTimeout(() => setEggKey(null), 2500);
    } else {
      b.timer = setTimeout(() => { b.count = 0; }, 600);
    }
  }, []);

  // Guest ("Try it") mode: a slim shell with no app nav — just the editor and
  // a prominent "Save & register" CTA. Guests are contained to the editor and
  // help; any other route bounces back to the editor (defence in depth — the
  // backend also blocks guests from matches/cups).
  if (user?.is_guest) {
    const allowed = location.pathname === "/team/edit" || location.pathname === "/help";
    if (!allowed) return <Navigate to="/team/edit" replace />;
    return (
      <div className="app-shell">
        <nav className="app-nav">
          <span className="app-nav-brand">Tactics</span>
          <div className="app-nav-links">
            <span className="guest-badge">{t("guest.badge")}</span>
            <NavLink to="/team/edit" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} title={t("nav.team")}>
              <Shirt size={18} />
            </NavLink>
            <NavLink to="/help" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} title={t("nav.help")}>
              <CircleHelp size={18} />
            </NavLink>
            <button className="guest-save-btn" onClick={() => setShowGuestRegister(true)}>
              {t("guest.saveAndRegister")}
            </button>
            <LanguageSelector />
          </div>
        </nav>
        <main className="app-content" ref={contentRef}>{children}</main>
        {showGuestRegister && (
          <Modal onClose={() => setShowGuestRegister(false)}>
            <GuestRegisterModal onClose={() => setShowGuestRegister(false)} />
          </Modal>
        )}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app-shell">
        <nav className="app-nav">
          <NavLink to="/" className="app-nav-brand" onClick={handleBrandClick}>Tactics</NavLink>
          <div className="app-nav-links">
            <NavLink to="/help" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} title={t("nav.help")}>
              <CircleHelp size={18} />
            </NavLink>
            <a href="/teachers/index.html" className="nav-link nav-link--text">{t("nav.teachers")}</a>
            <button className="nav-link nav-link--text" onClick={() => setShowLogin(true)}>{t("start.login")}</button>
            <button className="nav-link nav-link--text nav-link--accent" onClick={() => setShowSignup(true)}>{t("start.signup")}</button>
            <LanguageSelector />
          </div>
        </nav>
        <main className="app-content" ref={contentRef}>{children}</main>
        {showLogin  && <Modal onClose={() => setShowLogin(false)}><LoginForm  onSuccess={() => setShowLogin(false)}  onCancel={() => setShowLogin(false)}  /></Modal>}
        {showSignup && <Modal onClose={() => setShowSignup(false)}><SignupForm onClose={() => setShowSignup(false)} /></Modal>}
        {eggKey && <div className={`brand-easter-egg brand-easter-egg--${eggKey}`}>{t(`nav.${eggKey}Anon`, { defaultValue: t(`nav.${eggKey}`) })}</div>}
      </div>
    );
  }

  return (
    <div className="app-shell">
      <nav className="app-nav">
        <span className="app-nav-brand" onClick={handleBrandClick}>Tactics</span>
        <div className="app-nav-links">
          <NavLink to="/dashboard" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} title={t("nav.home")}>
            <House size={18} />
          </NavLink>
          <NavLink
            to={team ? "/team/edit" : "/team/create"}
            className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
            title={team?.name ?? t("nav.team")}
          >
            <Shirt size={18} />
            {!readiness.ready && (
              <span className="nav-team-warning" title={
                readiness.no_team            ? "No team created yet" :
                readiness.missing_tactics > 0 ? `${readiness.missing_tactics} player(s) without a tactic` :
                readiness.no_starter          ? "No kick-off player set" : ""
              }>⚠</span>
            )}
          </NavLink>
          <NavLink to="/profile" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} title={user?.name}>
            <User size={18} />
          </NavLink>
          <NavLink to="/cups" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} title={t("nav.cups")}>
            <Trophy size={18} />
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} title="Admin">
              <Shield size={18} />
            </NavLink>
          )}
          <NavLink to="/help" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} title={t("nav.help")}>
            <CircleHelp size={18} />
          </NavLink>
          {/* Dashboard already has the action centre with this same flow, so the
              nav button would be redundant there — only Team and Profile. */}
          {team && (location.pathname.startsWith("/team")
                    || location.pathname === "/profile") && (
            <NewMatchButton />
          )}
          <NotificationBell />
          <button className="nav-logout-btn" onClick={handleLogout} title={t("profile.logout")}>
            <LogOut size={18} />
          </button>
        </div>
      </nav>

      <main className="app-content" ref={contentRef}>
        {children}
      </main>

      {/* <AdBanner />  ← oculto hasta tener usuarios */}
      {eggKey && <div className={`brand-easter-egg brand-easter-egg--${eggKey}`}>{t(`nav.${eggKey}`, { name: user.name })}</div>}
    </div>
  );
}
